import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Pin, ExternalLink, FileText } from 'lucide-react';
import { Button, buttonVariants } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/client/components/ui/collapsible';
import { cn } from '@/client/lib/utils';
import { useEncodersStore } from '@/client/store/encodersStore';
import { AlertCard } from './alert-card';
import type { EncoderAlertPayload } from '@shared/types';

const ALERT_TYPE_LABELS: Record<string, string> = {
  offline: 'Offline',
  zero_bitrate: 'Zero Bitrate',
  bitrate_too_high: 'Bitrate Too High',
  bitrate_drop: 'Bitrate Drop',
  bitrate_unstable: 'Unstable Bitrate',
  frame_rate_drop: 'Frame Rate Drop',
  resolution_change: 'Resolution Changed',
};

interface GroupedAlertCardProps {
  encoderId: string;
  alerts: EncoderAlertPayload[];
  isInTriage: boolean;
  onPinToTriage: (encoderId: string) => void;
  isPinning?: boolean;
  onMute?: (encoderId: string, alertType: string) => void;
  onUnmute?: (encoderId: string, alertType: string) => void;
  onExport?: (encoderId: string) => void;
}

export function GroupedAlertCard({
  encoderId,
  alerts,
  isInTriage,
  onPinToTriage,
  isPinning,
  onMute,
  onUnmute,
  onExport,
}: GroupedAlertCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmingPin, setConfirmingPin] = useState(false);

  // Look up client name and stream name from encoders store
  const encoder = useEncodersStore((s) => s.getEncoder(encoderId));
  const clientName = encoder?.clientName;
  const streamName = alerts[0]?.streamName || encoderId;

  // Determine highest severity for border color
  const severityOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
  const highestSeverity = alerts.reduce((highest, alert) => {
    const currentOrder = severityOrder[alert.severity] ?? 3;
    const highestOrder = severityOrder[highest] ?? 3;
    return currentOrder < highestOrder ? alert.severity : highest;
  }, 'info' as 'critical' | 'warning' | 'info');

  // Count by severity
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;
  const infoCount = alerts.filter(a => a.severity === 'info').length;

  // Get unique alert types for summary
  const alertTypes = [...new Set(alerts.map(a => a.alertType))];

  const severityStyles = {
    critical: 'border-l-red-500 bg-red-50/10',
    warning: 'border-l-amber-500 bg-amber-50/10',
    info: 'border-l-blue-500 bg-blue-50/10',
  };

  const severityDotStyles = {
    critical: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          'rounded-lg border border-l-4 transition-colors',
          severityStyles[highestSeverity]
        )}
      >
        {/* Collapsed Header */}
        <div className="flex items-center gap-3 p-3">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>

          {/* Severity dots */}
          <div className="flex gap-0.5">
            {criticalCount > 0 && (
              <span className={cn('h-2.5 w-2.5 rounded-full', severityDotStyles.critical)} title={`${criticalCount} critical`} />
            )}
            {warningCount > 0 && (
              <span className={cn('h-2.5 w-2.5 rounded-full', severityDotStyles.warning)} title={`${warningCount} warning`} />
            )}
            {infoCount > 0 && (
              <span className={cn('h-2.5 w-2.5 rounded-full', severityDotStyles.info)} title={`${infoCount} info`} />
            )}
          </div>

          {/* Client name + Stream name / Encoder ID */}
          <span className="font-medium text-sm truncate flex-1">
            {clientName && <span className="text-muted-foreground">{clientName} / </span>}
            {streamName}
          </span>

          {/* Alert count badge */}
          <Badge variant="outline" className="text-xs">
            {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
          </Badge>

          {/* Alert type summary badges */}
          <div className="hidden md:flex gap-1">
            {alertTypes.slice(0, 3).map(type => (
              <Badge key={type} variant="secondary" className="text-xs">
                {ALERT_TYPE_LABELS[type] || type}
              </Badge>
            ))}
            {alertTypes.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{alertTypes.length - 3}
              </Badge>
            )}
          </div>

          {/* Pin to Triage button - two-step confirm */}
          {!isInTriage && (
            confirmingPin ? (
              <Button
                variant="default"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onPinToTriage(encoderId);
                  setConfirmingPin(false);
                }}
                onBlur={() => setConfirmingPin(false)}
                disabled={isPinning}
                autoFocus
              >
                <Pin className="h-3 w-3" />
                {isPinning ? 'Pinning...' : 'Confirm Pin'}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmingPin(true);
                }}
              >
                <Pin className="h-3 w-3" />
                Pin to Triage
              </Button>
            )
          )}
          {isInTriage && (
            <Badge variant="secondary" className="text-xs gap-1">
              <Pin className="h-3 w-3" />
              In Triage
            </Badge>
          )}
        </div>

        {/* Expanded Content - show individual alerts */}
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-0 border-t border-dashed ml-4">
            <div className="space-y-2 py-3">
              {alerts.map((alert) => (
                <AlertCard
                  key={`${alert.encoderId}-${alert.alertType}`}
                  alert={alert}
                  isInTriage={isInTriage}
                  onPinToTriage={onPinToTriage}
                  isPinning={isPinning}
                  isMuted={false}
                  onMute={onMute}
                  onUnmute={onUnmute}
                  onExport={onExport}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t">
              <Link
                to="/encoders/$encoderID"
                params={{ encoderID: encoderId }}
                className={buttonVariants({ variant: 'outline', size: 'sm', className: 'gap-1 text-xs' })}
              >
                <ExternalLink className="h-3 w-3" />
                View Encoder
              </Link>
              {onExport && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 text-xs"
                  onClick={() => onExport(encoderId)}
                >
                  <FileText className="h-3 w-3" />
                  Export Logs
                </Button>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
